import { TrainingsFilters } from '@core/constants/training-filters.constants';
import { ToolbarFiltersTypes } from '@core/constants/toolbar-filters.constants';

import {
  RouteActionButtonInterface,
  RouteActionInterface,
} from '../../interfaces/route-action.interface';

const saveButton: RouteActionButtonInterface = {
  action: 'doActionOne',
  label: 'common.save',
  color: 'yellow',
  class: ['filled', 'no-border'],
  name: 'save',
};

const cancelButton: RouteActionButtonInterface = {
  action: 'doActionTwo',
  label: '',
  icon: 'close',
  color: 'darkblue',
  name: 'cancel',
  class: ['small'],
};

const deleteButton: RouteActionButtonInterface = {
  action: 'doActionThree',
  label: 'common.delete',
  color: 'darkblue',
  name: 'delete',
};

const previewButton: RouteActionButtonInterface = {
  action: 'doActionFour',
  label: 'common.preview',
  color: 'darkblue',
  name: 'preview',
};

const saveAndSync: RouteActionButtonInterface = {
  action: 'doActionOne',
  label: 'settings.organization.saveAndSync',
  color: 'yellow',
  class: ['filled', 'no-border'],
  name: 'saveAndSync',
};

const filtersButton: RouteActionButtonInterface = {
  action: 'showFilters',
  label: 'common.filters',
  icon: 'filter_list',
  color: 'darkblue',
  name: 'filters',
  class: ['show-filters no-border no-padding'],
  hidden: true,
};

const newItemButton = (
  key: string,
  routerLink = '',
  canBeHidden = false,
): RouteActionButtonInterface => ({
  label: key,
  color: 'yellow',
  class: ['filled', 'no-border'],
  name: 'new',
  icon: 'add',
  routerLink,
  canBeHidden,
  hidden: true,
});

export const routeActions: RouteActionInterface[] = [
  {
    // Routes with navigation via toolbar
    routeNames: ['/courses/list'],
    buttons: [filtersButton],
    menu: {
      label: 'toolbar.newCourse',
      icon: 'add',
      color: 'yellow',
      class: ['filled', 'no-border'],
      buttons: [
        {
          action: 'createFromScratch',
          label: 'toolbar.createFromScratch',
          icon: 'school',
          name: 'createFromScratch',
          color: 'darkblue',
        },
        {
          action: 'guidedCourseCreation',
          label: 'toolbar.guidedCourseCreation',
          iconSvg: 'ai_icon',
          name: 'guidedCourseCreation',
          color: 'darkblue',
        },
        {
          action: 'importCourse',
          label: 'toolbar.importCourse',
          iconSvg: 'course_import',
          name: 'importCourse',
          color: 'darkblue',
        },
      ],
    },
    filters: [
      ToolbarFiltersTypes.courses,
      ToolbarFiltersTypes.categories,
      ToolbarFiltersTypes.languages,
      ToolbarFiltersTypes.author,
      ToolbarFiltersTypes.mandatory,
      ToolbarFiltersTypes.editors,
      ToolbarFiltersTypes.trainingAvailability,
    ],
    storeId: TrainingsFilters.coursesFilters,
  },
  {
    routeNames: ['/categories/list'],
    buttons: [
      newItemButton('toolbar.newCategory', '/categories/new/profile'),
      filtersButton,
    ],
    filters: [ToolbarFiltersTypes.categories],
    storeId: TrainingsFilters.categoriesFilters,
  },
  {
    routeNames: ['/learning-paths/list'],
    buttons: [
      newItemButton(
        'toolbar.newLearningPath',
        '/learning-paths/new/profile',
        true,
      ),
      filtersButton,
    ],
    filters: [
      ToolbarFiltersTypes.learningPaths,
      ToolbarFiltersTypes.categories,
      ToolbarFiltersTypes.languages,
      ToolbarFiltersTypes.author,
      ToolbarFiltersTypes.mandatory,
      ToolbarFiltersTypes.editors,
      ToolbarFiltersTypes.trainingAvailability,
    ],
    storeId: TrainingsFilters.learningPathsFilters,
  },
  {
    routeNames: ['/ilt-courses/list'],
    buttons: [
      newItemButton(
        'toolbar.newLiveTraining',
        '/ilt-courses/new/profile',
        true,
      ),
      filtersButton,
    ],
    filters: [
      ToolbarFiltersTypes.trainings,
      ToolbarFiltersTypes.categories,
      ToolbarFiltersTypes.languages,
      ToolbarFiltersTypes.author,
      ToolbarFiltersTypes.mandatory,
      ToolbarFiltersTypes.editors,
      ToolbarFiltersTypes.trainingAvailability,
    ],
    storeId: TrainingsFilters.trainingsFilters,
  },
  {
    routeNames: ['/ilt-courses/instructors-list'],
    buttons: [
      {
        action: 'doActionOne',
        label: 'common.today',
        color: 'darkblue',
        class: ['today'],
        name: 'addPeople',
        icon: 'today',
        hidden: true,
      },
      filtersButton,
    ],
    filters: [
      ToolbarFiltersTypes.trainingsLocal,
      ToolbarFiltersTypes.trainingsEvents,
    ],
    storeId: TrainingsFilters.instructorsFilters,
  },
  {
    routeNames: ['/courses/task-activity-review'],
    buttons: [filtersButton],
    filters: [
      ToolbarFiltersTypes.courses,
      ToolbarFiltersTypes.courseActivities,
      ToolbarFiltersTypes.members,
    ],
    storeId: TrainingsFilters.taskReviewFilters,
  },
  {
    routeNames: ['/courses/my'],
    buttons: [filtersButton],
    filters: [
      ToolbarFiltersTypes.coursesLocal,
      ToolbarFiltersTypes.categories,
      ToolbarFiltersTypes.courseStatus,
      ToolbarFiltersTypes.languages,
      ToolbarFiltersTypes.mandatory,
    ],
    // inversed with my catalogue due to backwards compatibility
    storeId: TrainingsFilters.trainingCatalogueFilters,
    sticky: true,
  },
  {
    routeNames: ['/ilt-courses/my'],
    buttons: [filtersButton],
    filters: [
      ToolbarFiltersTypes.trainingsLocal,
      ToolbarFiltersTypes.categories,
      ToolbarFiltersTypes.trainingStatus,
      ToolbarFiltersTypes.languages,
      ToolbarFiltersTypes.mandatory,
    ],
    storeId: TrainingsFilters.myIltCoursesFilters,
    sticky: true,
  },
  {
    routeNames: ['/learning-paths/my'],
    buttons: [filtersButton],
    filters: [
      ToolbarFiltersTypes.learningPathsLocal,
      ToolbarFiltersTypes.categories,
      ToolbarFiltersTypes.courseStatus,
      ToolbarFiltersTypes.languages,
      ToolbarFiltersTypes.mandatory,
    ],
    storeId: TrainingsFilters.myLearningPathsFilters,
    sticky: true,
  },
  {
    routeNames: ['/courses/catalogue'],
    buttons: [filtersButton],
    filters: [
      ToolbarFiltersTypes.type,
      ToolbarFiltersTypes.trainingsLocal,
      ToolbarFiltersTypes.coursesLocal,
      ToolbarFiltersTypes.learningPathsLocal,
      ToolbarFiltersTypes.categories,
      ToolbarFiltersTypes.courseStatus,
      ToolbarFiltersTypes.languages,
      ToolbarFiltersTypes.mandatory,
    ],
    // inversed with my courses due to backwards compatibility
    storeId: TrainingsFilters.myCoursesFilters,
    sticky: true,
  },
  {
    routeNames: ['/training-schedules/list'],
    buttons: [
      newItemButton('toolbar.newSchedule', '/training-schedules/new/profile'),
      filtersButton,
    ],
    filters: [
      ToolbarFiltersTypes.trainingSchedules,
      ToolbarFiltersTypes.languages,
      ToolbarFiltersTypes.author,
      ToolbarFiltersTypes.editors,
      ToolbarFiltersTypes.trainingAvailability,
    ],
    storeId: TrainingsFilters.trainingScheduleFilters,
  },
  {
    routeNames: [/^\/training-schedules\/[0-9]+\/cohorts$/],
    buttons: [newItemButton('toolbar.newCohort'), filtersButton],
    filters: [ToolbarFiltersTypes.cohorts],
    storeId: TrainingsFilters.trainingScheduleCohortsFilters,
  },
  {
    routeNames: ['/settings/custom-email/list'],
    buttons: [filtersButton],
    filters: [
      ToolbarFiltersTypes.languages,
      ToolbarFiltersTypes.emailTemplates,
      ToolbarFiltersTypes.emailTemplatesType,
    ],
    storeId: TrainingsFilters.emailTemplatesFilters,
  },
  {
    routeNames: [/^\/organizations\/[0-9]+\/members$/],
    buttons: [filtersButton],
    filters: [
      ToolbarFiltersTypes.searchTable,
      ToolbarFiltersTypes.hrStatus,
      ToolbarFiltersTypes.memberStatus,
      ToolbarFiltersTypes.subunits,
    ],
    storeId: TrainingsFilters.orgaMemberFilters,
  },
  {
    routeNames: ['/organizations/list'],
    buttons: [],
  },
  {
    routeNames: [/^\/organizations\/[0-9]+\/trainings$/],
    buttons: [
      {
        action: 'doActionOne',
        label: 'organization.trainings.assign',
        color: 'yellow',
        class: ['filled', 'no-border'],
        name: 'assignTrainings',
        hidden: true,
      },
      filtersButton,
    ],
    filters: [
      ToolbarFiltersTypes.searchTable,
      ToolbarFiltersTypes.type,
      ToolbarFiltersTypes.categories,
    ],
    storeId: TrainingsFilters.orgaTrainingFilters,
  },
  {
    routeNames: [/^\/organizations\/[0-9]+\/permissions$/],
    buttons: [
      {
        action: 'doActionOne',
        label: 'organization.permissions.addUser',
        color: 'darkblue',
        class: [],
        name: 'addPeople',
        icon: 'group_add',
        hidden: true,
      },
      {
        action: 'doActionTwo',
        label: 'common.save',
        color: 'yellow',
        class: ['filled', 'no-border'],
        name: 'savePermissions',
        hidden: true,
      },
    ],
  },
  {
    routeNames: ['/talents/list'],
    buttons: [
      newItemButton('toolbar.newMember', '/talents/new', true),
      filtersButton,
    ],
    menu: {
      label: 'toolbar.inviteToElearnio',
      icon: 'group_add',
      class: ['no-border', 'action-button-quaternary'],
      buttons: [
        {
          action: 'inviteByEmail',
          label: 'toolbar.inviteEmail',
          icon: 'mail',
          name: 'mail',
          color: 'darkblue',
          canBeHidden: true,
          hidden: true,
        },
        {
          action: 'inviteByCsv',
          label: 'toolbar.inviteCsv',
          icon: 'attach_file',
          color: 'darkblue',
          name: 'inviteCsv',
          canBeHidden: true,
          hidden: true,
        },
        {
          action: 'doActionOne',
          label: 'toolbar.resendInvites',
          icon: 'group',
          name: 'resendInvites',
          color: 'darkblue',
          canBeHidden: true,
          hidden: true,
        },
      ],
    },
    filters: [
      ToolbarFiltersTypes.members,
      ToolbarFiltersTypes.memberIdentifiers,
      ToolbarFiltersTypes.groups,
      ToolbarFiltersTypes.hireDate,
      ToolbarFiltersTypes.hrStatus,
      ToolbarFiltersTypes.memberStatus,
      ToolbarFiltersTypes.checkUserName,
    ],
    storeId: TrainingsFilters.peoplePeopleFilters,
  },
  {
    routeNames: ['/groups/list'],
    buttons: [
      newItemButton('toolbar.newGroup', '/groups/new/profile'),
      filtersButton,
    ],
    filters: [ToolbarFiltersTypes.groups],
    storeId: TrainingsFilters.peopleGroupFilters,
  },
  {
    routeNames: ['/assignment-approvals/list'],
    buttons: [filtersButton],
    filters: [ToolbarFiltersTypes.type, ToolbarFiltersTypes.members],
    storeId: TrainingsFilters.assignmentApprovalFilters,
  },
  {
    routeNames: ['/settings/certificates'],
    buttons: [newItemButton('common.new', '/settings/certificates/new')],
  },
  {
    routeNames: ['/settings/learning-history'],
    buttons: [newItemButton('common.new', '/settings/learning-history/new')],
  },
  {
    routeNames: ['/settings/integrations'],
    buttons: [newItemButton('common.new', '/settings/integrations/new')],
  },
  {
    routeNames: ['/settings/branding/list'],
    buttons: [newItemButton('common.new', '/settings/branding/new', true)],
  },
  {
    routeNames: ['/settings/news/list'],
    buttons: [newItemButton('common.new', '/settings/news/new', true)],
  },
  {
    routeNames: [
      '/reporting/organization',
      '/reporting/groups',
      '/reporting/talents',
      '/reporting/trainings',
      '/reporting/courses',
      '/reporting/ilt-courses',
      '/reporting/learning-paths',
      '/reporting/schedules',
      /^\/reporting\/courses\/\d+(?:&?[^=&]*=[^=&]*)*$/,
      /^\/reporting\/learning-paths\/\d+(?:&?[^=&]*=[^=&]*)*$/,
      /^\/reporting\/ilt-courses\/\d+(?:&?[^=&]*=[^=&]*)*$/,
      /^\/reporting\/schedules\/\d+\/cohorts$/,
      /^\/reporting\/schedules\/\d+\/cohorts\/\d+\/members$/,
      /^\/reporting\/talents\?.*/,
      /^\/reporting\/courses\?.*/,
      /^\/reporting\/trainings\?.*/,
      /^\/reporting\/learning-paths\?.*/,
      /^\/reporting\/schedules\?.*/,
      /^\/reporting\/ilt-courses\?.*/,
    ],
    buttons: [],
  },
  // Routes with actions via toolbar
  {
    routeNames: [
      '/categories/new/profile',
      '/groups/new/profile',
      '/talents/new',
      '/my/profile',
      '/settings/branding/new',
      '/settings/news/new',
      /^\/settings\/custom-email\/[0-9]+$/,
    ],
    buttons: [saveButton, cancelButton],
  },
  // Routes with actions via toolbar
  {
    routeNames: [
      '/settings/company',
      '/settings/authentication',
      '/settings/gamification',
      '/settings/self-registration',
    ],
    buttons: [saveButton, cancelButton],
    sticky: true,
  },
  {
    routeNames: [
      /^\/categories\/[0-9]+\/profile$/,
      /^\/groups\/[0-9]+\/profile$/,
      /^\/talents\/[0-9]+$/,
      /^\/settings\/news\/[0-9]+$/,
    ],
    buttons: [saveButton, deleteButton, cancelButton],
  },
  // Settings
  {
    routeNames: [
      '/settings/certificates/new',
      /^\/settings\/certificates\/[0-9]+$/,
    ],
    buttons: [saveButton, previewButton, cancelButton],
    sticky: true,
  },
  {
    routeNames: [
      '/settings/learning-history/new',
      /^\/settings\/learning-history\/[0-9]+$/,
    ],
    buttons: [saveButton, previewButton, cancelButton],
    sticky: true,
  },
  {
    routeNames: [
      '/settings/integrations/new',
      /^\/settings\/integrations\/[0-9]+$/,
    ],
    buttons: [
      {
        action: 'doActionThree',
        label: 'settings.integrations.personio.sync',
        color: 'darkblue',
        class: ['filled', 'no-border'],
        name: 'sync',
        hidden: true,
      },
      {
        action: 'doActionOne',
        label: 'common.save',
        color: 'yellow',
        class: ['filled', 'no-border'],
        name: 'save',
        hidden: true,
      },
      cancelButton,
    ],
    sticky: true,
  },
  {
    routeNames: ['/settings/organization'],
    buttons: [saveAndSync, cancelButton],
    sticky: true,
  },
  {
    routeNames: [/^\/settings\/branding\/[0-9]+$/],
    buttons: [saveButton, deleteButton, cancelButton],
    sticky: true,
  },
  {
    routeNames: [/^\/courses\/task-activity-review\/[0-9]+$/],
    buttons: [saveButton],
  },
];
